import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import type { CentroVisit, Equipment } from 'shared';
import { ACTIVITY_SESSION_JOIN_PATH, parseActivitySessionQR } from 'shared';
import QRScanner from '@/components/QRScanner';
import {
  isAlreadyCheckedIn,
  useCheckInCentro,
  useCheckOutCentro,
  useCurrentCentro,
} from '@/hooks/useCentroVisits';
import { useEquipmentsByCentro } from '@/hooks/useEquipments';
import { useMyActiveReservations } from '@/hooks/useReservations';
import { checkInErrorMessage, RETURN_EQUIPMENT_BEFORE_CHECKOUT_MESSAGE } from '@/lib/errors';
import { describeFreeUnits } from '@/lib/equipmentUnits';
import { useAuthStore } from '@/stores/auth';

type DialogState =
  | { kind: 'closed' }
  | { kind: 'block'; activeVisit: CentroVisit; pendingScan: boolean };

// Shorten the freshness window for this screen so that records updated on the
// backend (centro visit, equipment state, open reservations) surface on the
// next focus/navigation refetch instead of after the hooks' longer defaults.
const LIVE_STALE_TIME_MS = 5_000;

const formatTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

// Equipamento is shown to participants only when it can actually be used at
// the centro they checked into: anything Inativo (or soft-deleted) is hidden.
function isAvailableForParticipants(equipment: Equipment): boolean {
  return equipment.estado !== 'Inativo' && !equipment.deletedAt;
}

export default function HomePage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const currentQuery = useCurrentCentro(LIVE_STALE_TIME_MS);
  const checkIn = useCheckInCentro();
  const checkOut = useCheckOutCentro();

  const [scannerOpen, setScannerOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });

  const firstName = user?.name?.split(' ')[0] ?? 'Olá';
  const activeCentro = currentQuery.data ?? null;
  const equipmentsQuery = useEquipmentsByCentro(activeCentro?.digitalCenterId, LIVE_STALE_TIME_MS);
  const availableEquipments =
    equipmentsQuery.data?.equipments.filter(isAvailableForParticipants) ?? [];
  // The badge counts only equipamentos shown as "Disponível"; anything "Em
  // utilização" is still listed but must not inflate the available count.
  const availableCount = availableEquipments.filter(
    (eq) => eq.estado !== 'Em utilização' && eq.estado !== 'Reservado',
  ).length;

  // A participant is only ever in one Centro at a time, so any open reservation
  // is held at the active Centro. We block check-out until they are all
  // returned; the count is only trusted once the query has resolved.
  const participantId = typeof user?.id === 'number' ? user.id : undefined;
  const reservationsQuery = useMyActiveReservations(participantId, LIVE_STALE_TIME_MS);
  const openReservationCount = reservationsQuery.data?.assignments?.length ?? 0;
  const hasOpenReservations = openReservationCount > 0;

  const openScanner = () => setScannerOpen(true);

  const handleDecoded = async (qrCode: string) => {
    setScannerOpen(false);

    // An activity's QR doubles as the Centro check-in, so it is welcome here.
    const sessionToken = parseActivitySessionQR(qrCode);
    if (sessionToken !== null) {
      navigate(`${ACTIVITY_SESSION_JOIN_PATH}${sessionToken}`);
      return;
    }

    try {
      await checkIn.mutateAsync({ qrCode });
    } catch (err) {
      if (isAlreadyCheckedIn(err)) {
        setDialog({ kind: 'block', activeVisit: err.body.activeVisit, pendingScan: true });
      }
    }
  };

  const handleCheckOut = async (thenScan: boolean) => {
    // Strong enforcement: never terminate a visit while equipment is still out.
    // Guards both the main "Terminar visita" button and the BlockingDialog path.
    if (hasOpenReservations) {
      return;
    }
    try {
      await checkOut.mutateAsync();
      setDialog({ kind: 'closed' });
      if (thenScan) setScannerOpen(true);
    } catch {
      // Failure already surfaced through mutation state.
    }
  };

  return (
    <>
      <Helmet>
        <title>Início | AICD</title>
      </Helmet>

      <div className="space-y-6">
        <header className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Olá,</p>
            <h1 className="mt-0.5 text-2xl font-extrabold tracking-tight text-foreground">
              {firstName}
            </h1>
            {typeof user?.id === 'number' && (
              <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                ID: {user.id}
              </p>
            )}
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-base font-bold text-primary-foreground shadow-sm">
            {firstName.slice(0, 1).toUpperCase()}
          </div>
        </header>

        {activeCentro && (
          <section
            aria-live="polite"
            className="rounded-2xl border border-available bg-available-soft p-4 shadow-sm"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-available">
              Está no Centro
            </p>
            <p className="mt-1 text-lg font-extrabold text-foreground">
              {activeCentro.digitalCenterName}
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Desde {formatTime(activeCentro.checkedInAt)}
            </p>
            <button
              type="button"
              onClick={() => handleCheckOut(false)}
              disabled={checkOut.isPending || hasOpenReservations}
              className="mt-3 w-full rounded-xl bg-available px-4 py-3 text-sm font-bold text-white shadow disabled:opacity-60"
            >
              {checkOut.isPending ? 'A terminar visita…' : 'Terminar visita'}
            </button>

            {hasOpenReservations && (
              <div
                role="alert"
                className="mt-3 rounded-xl border border-busy/50 bg-busy-soft p-3 text-sm text-busy"
              >
                <p>{RETURN_EQUIPMENT_BEFORE_CHECKOUT_MESSAGE}</p>
                <Link
                  to="/reservations"
                  className="mt-2 inline-block font-bold underline"
                >
                  Ver reservas
                </Link>
              </div>
            )}
          </section>
        )}

        {!activeCentro && (
          <button
            type="button"
            onClick={openScanner}
            disabled={checkIn.isPending}
            className="relative w-full overflow-hidden rounded-3xl bg-gradient-to-br from-[#512f89] to-[#6740a8] p-5 text-left text-white shadow-lg transition-transform active:scale-[0.99] disabled:opacity-70"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-[#4ab065]/30 blur-3xl"
            />
            <div className="relative flex items-center gap-4">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10">
                <ScanGlyph />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-black tracking-tight">Ler Centro</p>
                <p className="mt-0.5 text-xs text-white/80">
                  Aponte a câmara para o código QR à entrada.
                </p>
              </div>
              <span className="flex-shrink-0 text-[#4ab065]">
                <ArrowGlyph />
              </span>
            </div>
          </button>
        )}

        {checkIn.isError && !isAlreadyCheckedIn(checkIn.error) && (
          <p role="alert" className="rounded-xl border border-busy/50 bg-busy-soft p-3 text-sm text-busy">
            {checkInErrorMessage(checkIn.error)}
          </p>
        )}

        {activeCentro && (
          <section aria-labelledby="equipamentos-heading">
            <div className="mb-3 flex items-center justify-between px-1">
              <div>
                <h2
                  id="equipamentos-heading"
                  className="text-xs font-bold uppercase tracking-widest text-muted-foreground"
                >
                  Equipamentos disponíveis
                </h2>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {activeCentro.digitalCenterName}
                </p>
              </div>
              {equipmentsQuery.data && (
                <span className="rounded-md bg-input-soft px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                  {availableCount}
                </span>
              )}
            </div>

            {equipmentsQuery.isLoading && <EquipmentSkeleton />}

            {equipmentsQuery.isError && !equipmentsQuery.isLoading && (
              <div
                role="alert"
                className="rounded-2xl border border-busy/50 bg-busy-soft p-4 text-sm text-busy"
              >
                Não foi possível carregar os equipamentos. Tente novamente mais tarde.
              </div>
            )}

            {!equipmentsQuery.isLoading &&
              !equipmentsQuery.isError &&
              equipmentsQuery.data &&
              availableEquipments.length === 0 && (
                <div className="rounded-2xl border border-dashed border-border-strong bg-card p-6 text-center text-sm text-muted-foreground">
                  Não existem equipamentos disponíveis neste Centro.
                </div>
              )}

            {availableEquipments.length > 0 && (
              <ul className="space-y-2">
                {availableEquipments.map((eq) => (
                  <li
                    key={eq.id}
                    className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm"
                  >
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <EquipmentGlyph />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-foreground">
                        {eq.equipamento}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {eq.tipoCategoria}
                        {eq.requerFormacao && eq.requerFormacao !== 'Baixa'
                          ? ` · Formação ${eq.requerFormacao}`
                          : ''}
                        {describeFreeUnits(eq)}
                      </p>
                    </div>
                    <EquipmentEstadoBadge estado={eq.estado} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      {scannerOpen && (
        <QRScanner
          onDecoded={handleDecoded}
          onCancel={() => setScannerOpen(false)}
          title="Ler QR do Centro"
          promptHint="Toque para iniciar a câmara e ler o código QR do Centro."
          instructions="Aponte a câmara para o código QR do Centro para fazer check-in."
        />
      )}

      {dialog.kind === 'block' && (
        <BlockingDialog
          centroName={dialog.activeVisit.digitalCenterName}
          checkingOut={checkOut.isPending}
          onClose={() => setDialog({ kind: 'closed' })}
          onCheckOut={() => handleCheckOut(dialog.pendingScan)}
        />
      )}
    </>
  );
}

function EquipmentSkeleton() {
  return (
    <ul className="space-y-2">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3"
        >
          <div className="h-11 w-11 flex-shrink-0 animate-pulse rounded-2xl bg-input-soft" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-2/3 animate-pulse rounded bg-input-soft" />
            <div className="h-2 w-1/3 animate-pulse rounded bg-input-soft" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EquipmentEstadoBadge({ estado }: { estado: Equipment['estado'] }) {
  if (estado === 'Em utilização') {
    return (
      <span className="rounded-md bg-busy-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-busy">
        Em uso
      </span>
    );
  }
  if (estado === 'Reservado') {
    return (
      <span className="rounded-md bg-busy-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-busy">
        Reservado
      </span>
    );
  }
  return (
    <span className="rounded-md bg-available-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-available">
      Disponível
    </span>
  );
}

interface BlockingDialogProps {
  centroName: string;
  checkingOut: boolean;
  onClose: () => void;
  onCheckOut: () => void;
}

function BlockingDialog({ centroName, checkingOut, onClose, onCheckOut }: BlockingDialogProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-dialog-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-5"
    >
      <div className="w-full max-w-sm rounded-3xl bg-surface p-6 shadow-lg">
        <h2 id="checkout-dialog-title" className="text-lg font-extrabold text-foreground">
          Termine primeiro a visita atual
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Ainda está em <span className="font-semibold">{centroName}</span>. Termine essa
          visita antes de ler outro Centro.
        </p>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-border bg-input-soft px-4 py-3 text-sm font-bold text-muted-foreground"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onCheckOut}
            disabled={checkingOut}
            className="flex-1 rounded-xl bg-available px-4 py-3 text-sm font-bold text-white shadow disabled:opacity-60"
          >
            {checkingOut ? 'A terminar…' : 'Terminar visita'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScanGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M4 7V5a1 1 0 011-1h2" strokeLinecap="round" />
      <path d="M20 7V5a1 1 0 00-1-1h-2" strokeLinecap="round" />
      <path d="M4 17v2a1 1 0 001 1h2" strokeLinecap="round" />
      <path d="M20 17v2a1 1 0 01-1 1h-2" strokeLinecap="round" />
      <path d="M4 12h16" strokeLinecap="round" />
    </svg>
  );
}

function ArrowGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2.5}>
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EquipmentGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="5" width="18" height="12" rx="2" strokeLinejoin="round" />
      <path d="M8 21h8" strokeLinecap="round" />
      <path d="M12 17v4" strokeLinecap="round" />
    </svg>
  );
}
